import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Trees, Church, ShoppingBag, UtensilsCrossed, Mountain } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  cultura: Church,
  naturaleza: Trees,
  compras: ShoppingBag,
  gastronomia: UtensilsCrossed
};

const categoryColors = {
  cultura: 'bg-purple-100 text-purple-700',
  naturaleza: 'bg-green-100 text-green-700',
  compras: 'bg-amber-100 text-amber-700',
  gastronomia: 'bg-red-100 text-red-700'
};

const Mazamitla = () => {
  const [pointsOfInterest, setPointsOfInterest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { t, language } = useLanguage();
  const { getWhatsAppLink } = useBusinessInfo();

  useEffect(() => {
    const fetchPOI = async () => {
      try {
        const response = await axios.get(`${API}/points-of-interest`);
        setPointsOfInterest(response.data);
      } catch (error) {
        console.error('Error fetching points of interest:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPOI();
  }, []);

  const categories = ['all', ...new Set(pointsOfInterest.map(p => p.category))];
  const filteredPOI = filter === 'all' 
    ? pointsOfInterest 
    : pointsOfInterest.filter(p => p.category === filter);

  // Category translations
  const categoryTranslations = {
    es: { all: 'Todos', cultura: 'Cultura', naturaleza: 'Naturaleza', compras: 'Compras', gastronomia: 'Gastronomía' },
    en: { all: 'All', cultura: 'Culture', naturaleza: 'Nature', compras: 'Shopping', gastronomia: 'Gastronomy' }
  };

  // Activity translations
  const activities = language === 'es' ? [
    { icon: Trees, title: 'Senderismo', desc: 'Explora los senderos entre bosques de pino y encino' },
    { icon: Mountain, title: 'Paseos a caballo', desc: 'Recorre el campo montado a caballo' },
    { icon: UtensilsCrossed, title: 'Gastronomía', desc: 'Prueba el borrego, quesadillas y postres típicos' },
    { icon: ShoppingBag, title: 'Artesanías', desc: 'Llévate recuerdos únicos hechos a mano' }
  ] : [
    { icon: Trees, title: 'Hiking', desc: 'Explore trails through pine and oak forests' },
    { icon: Mountain, title: 'Horseback riding', desc: 'Ride through the countryside on horseback' },
    { icon: UtensilsCrossed, title: 'Gastronomy', desc: 'Try lamb, quesadillas and traditional desserts' },
    { icon: ShoppingBag, title: 'Crafts', desc: 'Take home unique handmade souvenirs' }
  ];

  return (
    <div data-testid="mazamitla-page" className="min-h-screen bg-[#F5F2EB] pt-24">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1606168329341-f05186dfaac9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxwaW5lJTIwZm9yZXN0JTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzcwODQ0OTg4fDA&ixlib=rb-4.1.0&q=85"
            alt="Mazamitla"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2F3E30]/90 via-[#2F3E30]/50 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end pb-16">
          <div className="container-ribba">
            <span className="inline-block text-[#C6A87C] text-sm font-medium tracking-widest uppercase mb-3">
              {language === 'es' ? 'Descubre' : 'Discover'}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-white font-bold mb-4">
              Mazamitla
            </h1>
            <p className="text-white/80 text-lg max-w-2xl">
              {language === 'es' 
                ? 'Pueblo Mágico rodeado de bosques de pino, cascadas y tradiciones. Un destino perfecto para desconectarte y disfrutar de la naturaleza.'
                : 'Magic Town surrounded by pine forests, waterfalls and traditions. A perfect destination to disconnect and enjoy nature.'}
            </p>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="container-ribba">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
                {t('mazamitla.title')}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] font-bold mt-3 mb-6">
                {language === 'es' ? 'La Suiza Mexicana' : 'The Mexican Switzerland'}
              </h2>
              <div className="space-y-4 text-[#5A5A5A] leading-relaxed">
                <p>
                  {language === 'es' 
                    ? 'Mazamitla es uno de los destinos más encantadores de Jalisco, conocido como "La Suiza Mexicana" por sus impresionantes paisajes de montaña y bosques de pino.'
                    : 'Mazamitla is one of the most charming destinations in Jalisco, known as "The Mexican Switzerland" for its stunning mountain landscapes and pine forests.'}
                </p>
                <p>
                  {language === 'es'
                    ? 'Ubicado a 2,200 metros sobre el nivel del mar en la Sierra del Tigre, este Pueblo Mágico ofrece un clima templado durante todo el año, ideal para escapar del calor de la ciudad y disfrutar de actividades al aire libre.'
                    : 'Located at 2,200 meters above sea level in the Sierra del Tigre, this Magic Town offers a temperate climate year-round, ideal for escaping the city heat and enjoying outdoor activities.'}
                </p>
                <p>
                  {language === 'es'
                    ? 'Sus calles empedradas, casas con techos de teja roja y la calidez de su gente te harán sentir como en casa desde el primer momento.'
                    : 'Its cobblestone streets, houses with red tile roofs and the warmth of its people will make you feel at home from the first moment.'}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4 bg-[#F5F2EB] rounded-sm">
                  <span className="block text-3xl font-heading font-bold text-[#2F3E30]">2,200</span>
                  <span className="text-sm text-[#5A5A5A]">{language === 'es' ? 'msnm' : 'masl'}</span>
                </div>
                <div className="text-center p-4 bg-[#F5F2EB] rounded-sm">
                  <span className="block text-3xl font-heading font-bold text-[#2F3E30]">18°C</span>
                  <span className="text-sm text-[#5A5A5A]">{language === 'es' ? 'promedio' : 'average'}</span>
                </div>
                <div className="text-center p-4 bg-[#F5F2EB] rounded-sm">
                  <span className="block text-3xl font-heading font-bold text-[#2F3E30]">130</span>
                  <span className="text-sm text-[#5A5A5A]">{language === 'es' ? 'km de GDL' : 'km from GDL'}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[3/4] rounded-sm overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1592966281550-a11d356bbab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxNYXphbWl0bGElMjBwdWVibG8lMjBtYWdpY28lMjBjaHVyY2h8ZW58MHx8fHwxNzcwODQ0OTU2fDA&ixlib=rb-4.1.0&q=85"
                    alt="Mazamitla pueblo"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[3/4] rounded-sm overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1588289654920-bd80280ab584?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxmb3Jlc3QlMjB3YXRlcmZhbGwlMjBuYXR1cmUlMjBtZXhpY298ZW58MHx8fHwxNzcwODQ0OTgyfDA&ixlib=rb-4.1.0&q=85"
                    alt={language === 'es' ? 'Cascada Mazamitla' : 'Mazamitla Waterfall'}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Points of Interest */}
      <section className="py-16">
        <div className="container-ribba">
          <div className="text-center mb-12">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              {language === 'es' ? 'Qué hacer' : 'What to do'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] font-bold mt-3 mb-4">
              {t('mazamitla.pointsOfInterest')}
            </h2>
            <p className="text-[#5A5A5A] max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Descubre los lugares más emblemáticos de Mazamitla y planifica tu aventura.'
                : 'Discover the most iconic places in Mazamitla and plan your adventure.'}
            </p>
          </div>

          {/* Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`poi-filter-${cat}`}
                className={`px-5 py-2 rounded-sm text-sm font-medium capitalize transition-all ${
                  filter === cat
                    ? 'bg-[#2F3E30] text-white'
                    : 'bg-white text-[#5A5A5A] hover:bg-[#E5E0D6]'
                }`}
              >
                {categoryTranslations[language][cat] || cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPOI.map((poi) => {
                const Icon = categoryIcons[poi.category] || Mountain;
                return (
                  <div
                    key={poi.id}
                    data-testid={`poi-card-${poi.id}`}
                    className="bg-white rounded-sm overflow-hidden card-shadow group"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={poi.image}
                        alt={poi.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-3 py-1 rounded-sm text-xs font-medium capitalize ${categoryColors[poi.category] || 'bg-gray-100 text-gray-700'}`}>
                          {categoryTranslations[language][poi.category] || poi.category}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                        {poi.name}
                      </h3>
                      <p className="text-[#5A5A5A] text-sm leading-relaxed">
                        {poi.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Activities */}
      <section className="py-16 bg-white">
        <div className="container-ribba">
          <div className="text-center mb-12">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              {t('mazamitla.activities')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] font-bold mt-3 mb-4">
              {language === 'es' ? 'Qué hacer en Mazamitla' : 'What to do in Mazamitla'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {activities.map((activity, idx) => (
              <div key={idx} className="text-center p-6 bg-[#F5F2EB] rounded-sm">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <activity.icon className="w-7 h-7 text-[#2F3E30]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-2">
                  {activity.title}
                </h3>
                <p className="text-[#5A5A5A] text-sm">
                  {activity.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to get there */}
      <section className="py-16 bg-[#2F3E30] text-white">
        <div className="container-ribba">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
                {t('contact.location')}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mt-3 mb-6">
                {language === 'es' ? 'Cómo llegar' : 'How to get there'}
              </h2>
              <div className="space-y-4 text-white/80">
                <p>
                  {language === 'es'
                    ? 'Mazamitla se encuentra a aproximadamente 130 km de Guadalajara, en la región sureste de Jalisco.'
                    : 'Mazamitla is located approximately 130 km from Guadalajara, in the southeast region of Jalisco.'}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#C6A87C] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#1A1A1A] font-bold text-sm">1</span>
                    </div>
                    <p>
                      <strong className="text-white">{language === 'es' ? 'Desde Guadalajara:' : 'From Guadalajara:'}</strong>{' '}
                      {language === 'es' 
                        ? 'Toma la carretera 54D hacia Colima hasta Jiquilpan, luego desvío a Mazamitla (aprox. 2h).'
                        : 'Take highway 54D towards Colima to Jiquilpan, then turn off to Mazamitla (approx. 2h).'}
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#C6A87C] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#1A1A1A] font-bold text-sm">2</span>
                    </div>
                    <p>
                      <strong className="text-white">{language === 'es' ? 'Desde Ciudad de México:' : 'From Mexico City:'}</strong>{' '}
                      {language === 'es'
                        ? 'Carretera hacia Guadalajara, desvío en La Barca hacia Sahuayo y Mazamitla (aprox. 6h).'
                        : 'Highway to Guadalajara, turn off at La Barca towards Sahuayo and Mazamitla (approx. 6h).'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 p-8 rounded-sm">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-8 h-8 text-[#C6A87C]" />
                <div>
                  <h3 className="font-heading text-xl font-semibold">Cabañas RIBBA</h3>
                  <p className="text-white/70 text-sm">Mazamitla, Jalisco, México</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-6">
                {language === 'es'
                  ? 'Nuestras cabañas están ubicadas en un exclusivo fraccionamiento con seguridad 24/7, a solo 5 minutos del centro de Mazamitla.'
                  : 'Our cabins are located in an exclusive gated community with 24/7 security, just 5 minutes from downtown Mazamitla.'}
              </p>
              <a
                href={getWhatsAppLink(language === 'es' ? 'Hola, me gustaría saber la ubicación exacta de las cabañas' : 'Hello, I would like to know the exact location of the cabins')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-[#1A1A1A] font-medium rounded-sm hover:bg-white transition-colors"
              >
                {language === 'es' ? 'Pedir ubicación por WhatsApp' : 'Request location via WhatsApp'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mazamitla;
